import axios from 'axios';
import { Function, AuthResponse, LoginCredentials, Variable, User } from '../types';

const API_BASE_URL = import.meta.env?.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  verify: async (): Promise<{ valid: boolean; user: User | null }> => {
    const response = await api.get('/auth/verify');
    return response.data;
  },
};

export const functionsService = {
  getAll: async (): Promise<Function[]> => {
    const response = await api.get('/functions');
    return response.data;
  },

  getById: async (id: string): Promise<Function> => {
    const response = await api.get(`/functions/${id}`);
    return response.data;
  },

  create: async (name: string, code: string): Promise<Function> => {
    const response = await api.post('/functions', { name, code });
    return response.data;
  },

  update: async (id: string, code: string): Promise<Function> => {
    const response = await api.put(`/functions/${id}`, { code });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/functions/${id}`);
  },

  getUrl: async (id: string): Promise<{ url: string }> => {
    // Generate the public URL directly instead of calling the backend
    const publicUrl = `https://functions.digitalaisolutions.de/api/execute/${id}`;
    return { url: publicUrl };
  },

  execute: async (id: string, payload: unknown): Promise<unknown> => {
    const response = await api.post(`/execute/${id}`, payload);
    return response.data;
  },

  restart: async (id: string): Promise<void> => {
    await api.post(`/functions/${id}/restart`);
  },

  stop: async (id: string): Promise<void> => {
    await api.post(`/functions/${id}/stop`);
  },

  getLogs: async (id: string): Promise<{ logs: string }> => {
    const response = await api.get(`/functions/${id}/logs`);
    return response.data;
  },

  getHealth: async (id: string): Promise<{ status: 'healthy' | 'unhealthy' | 'not_running' }> => {
    try {
      const response = await api.get(`/execute/${id}/health`);
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } };
      if (err.response?.status === 404) {
        return { status: 'not_running' };
      }
      return { status: 'unhealthy' };
    }
  },

  // File management
  getFiles: async (id: string): Promise<string[]> => {
    const response = await api.get(`/functions/${id}/files`);
    return response.data;
  },

  getFileContent: async (id: string, filename: string): Promise<{ content: string }> => {
    const response = await api.get(`/functions/${id}/files/${filename}`);
    return response.data;
  },

  updateFile: async (id: string, filename: string, content: string): Promise<void> => {
    await api.put(`/functions/${id}/files/${filename}`, { content });
  },

  renameFile: async (id: string, oldFilename: string, newFilename: string): Promise<void> => {
    await api.put(`/functions/${id}/files/${encodeURIComponent(oldFilename)}/rename`, { newFilename });
  },

  deleteFile: async (id: string, filename: string): Promise<void> => {
    await api.delete(`/functions/${id}/files/${filename}`);
  },
};

export const variablesService = {
  getAll: async (): Promise<Variable[]> => {
    const response = await api.get('/variables');
    return response.data;
  },

  getById: async (id: string): Promise<Variable> => {
    const response = await api.get(`/variables/${id}`);
    return response.data;
  },

  getByKey: async (key: string): Promise<Variable> => {
    const response = await api.get(`/variables/key/${key}`);
    return response.data;
  },

  create: async (key: string, value: string): Promise<Variable> => {
    const response = await api.post('/variables', { key, value });
    return response.data;
  },

  createMultiple: async (variables: { key: string; value: string }[]): Promise<Variable[]> => {
    const response = await api.post('/variables/batch', { variables });
    return response.data;
  },

  update: async (id: string, key: string, value: string): Promise<Variable> => {
    const response = await api.put(`/variables/${id}`, { key, value });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/variables/${id}`);
  },

  deleteByKey: async (key: string): Promise<void> => {
    const variable = await variablesService.getByKey(key);
    if (variable) {
      await variablesService.delete(variable.id);
    }
  },

  syncMissingToFunctions: async (): Promise<{ updated: number; total: number; addedPerFunction: Record<string, number> }> => {
    const response = await api.post('/variables/sync-missing');
    return response.data;
  },
};
